// referralService.js

// Use our backend API to proxy calls to Partner Dashboard (avoids CORS issues)
const getBackendUrl = () => {
  return process.env.REACT_APP_BACKEND_URL || '';
};

/**
 * Validate a partner referral code (via backend proxy)
 */
export async function validatePartnerCode(code) {
  try {
    const response = await fetch(`${getBackendUrl()}/api/referral/validate?ref=${code}`);
    return await response.json();
  } catch (error) {
    console.error('Failed to validate partner code:', error);
    return { success: false, error: 'Failed to validate' };
  }
}

/**
 * Track a new referral when user signs up (via backend proxy)
 */
export async function trackReferral({ refCode, customerEmail, customerName, plan = 'basic' }) {
  try {
    const response = await fetch(`${getBackendUrl()}/api/referral/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ref: refCode,
        customer_email: customerEmail,
        customer_name: customerName,
        plan: plan
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Failed to track referral:', error);
    return { success: false, error: 'Failed to track' };
  }
}

/**
 * Upgrade referral when user upgrades their plan (via backend proxy)
 */
export async function upgradeReferral(referralId, newPlan) {
  try {
    const response = await fetch(`${getBackendUrl()}/api/referral/upgrade/${referralId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        new_plan: newPlan
      })
    });
    return await response.json();
  } catch (error) {
    console.error('Failed to upgrade referral:', error);
    return { success: false, error: 'Failed to upgrade' };
  }
}

/**
 * Downgrade referral when user downgrades their plan
 */
export async function downgradeReferral(referralId, newPlan) {
  try {
    // If downgrading to free, use churn endpoint
    if (newPlan === 'free') {
      return await churnReferral(referralId);
    }
    
    // Otherwise use upgrade endpoint with lower plan
    return await upgradeReferral(referralId, newPlan);
  } catch (error) {
    console.error('Failed to downgrade referral:', error);
    return { success: false, error: 'Failed to downgrade' };
  }
}

/**
 * Activate referral when user makes first payment (via backend proxy)
 */
export async function activateReferral(referralId) {
  try {
    const response = await fetch(`${getBackendUrl()}/api/referral/activate/${referralId}`, {
      method: 'POST'
    });
    return await response.json();
  } catch (error) {
    console.error('Failed to activate referral:', error);
    return { success: false, error: 'Failed to activate' };
  }
}

/**
 * Mark referral as churned when user cancels (via backend proxy)
 */
export async function churnReferral(referralId) {
  try {
    const response = await fetch(`${getBackendUrl()}/api/referral/churn/${referralId}`, {
      method: 'POST'
    });
    return await response.json();
  } catch (error) {
    console.error('Failed to churn referral:', error);
    return { success: false, error: 'Failed to churn' };
  }
}

/**
 * Save referral code to localStorage
 */
export function saveReferralCode(code) {
  localStorage.setItem('referral_code', code);
  localStorage.setItem('referral_timestamp', Date.now().toString());
}

/**
 * Get saved referral code from localStorage
 */
export function getReferralCode() {
  return localStorage.getItem('referral_code');
}

/**
 * Clear referral code from localStorage
 */
export function clearReferralCode() {
  localStorage.removeItem('referral_code');
  localStorage.removeItem('referral_timestamp');
}
